import { useMutation, useQueryClient } from '@tanstack/react-query';
import { offerUnits, offers } from '@ooh/contracts';
import type { CreateOffer } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import {
  offerAvailabilityQueryKey,
  offerDetailQueryKey,
  offerListsQueryKeyPrefix,
  offerUnitsQueryKeyPrefix,
} from '../lib/offer-query-keys';

export function useCreateOffer() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateOffer) => api.call(offers.create, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerListsQueryKeyPrefix });
    },
  });
}

interface AddUnitsToOfferVars {
  offerId: number;
  mediaUnitIds: number[];
}

/** Adds media units to a draft offer without any drawer UI, so map and detail quick-add can share it. */
export function useAddUnitsToOffer() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ offerId, mediaUnitIds }: AddUnitsToOfferVars) =>
      api.call(offerUnits.add, { offerId, mediaUnitIds }),
    onSuccess: async (_response, { offerId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: offerUnitsQueryKeyPrefix(offerId) }),
        queryClient.invalidateQueries({ queryKey: offerDetailQueryKey(String(offerId)) }),
        queryClient.invalidateQueries({ queryKey: offerAvailabilityQueryKey(offerId) }),
      ]);
    },
  });
}
